import React, { FC } from 'react';
import { Popover } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { currencyFormatter } from '../../utils/DataFormatterUtils';
import {
	ConfidenceLabel,
	CostPressureDimension,
	DowntimeDimension,
	RemainingLifeDimension,
} from '../../types/asset_replacement_score';
import { CtaLink, ctaLinksFromMissingInputs } from './cta';
import AssetInvoice from '../asset_invoice/AssetInvoice';

// -- Confidence colour + icon ------------------------------------------------

export const CONFIDENCE_TEXT_COLOR: Record<ConfidenceLabel, string> = {
	high: 'text-green-600',
	medium: 'text-sunsetOrange-8',
	low: 'text-red-500',
	insufficient_data: 'text-grey-7',
};

export const confidenceDisplayLabel = (level: ConfidenceLabel): string => {
	if (level === 'insufficient_data') return 'Unavailable';
	return level.charAt(0).toUpperCase() + level.slice(1);
};

// Props beyond `level` / `className` are forwarded to the underlying AntD icon
// so that Popover / Tooltip can attach onMouseEnter etc. via cloneElement.
type ConfidenceIconProps = React.HTMLAttributes<HTMLSpanElement> & {
	level: ConfidenceLabel;
};
export const ConfidenceIcon: FC<ConfidenceIconProps> = ({ level, className, ...rest }) => {
	const finalClass = `${CONFIDENCE_TEXT_COLOR[level]} ${className || ''}`.trim();
	const Icon = level === 'high' ? InfoCircleOutlined : ExclamationCircleOutlined;
	return <Icon className={finalClass} {...rest} />;
};

export const ConfidenceLine: FC<{ level: ConfidenceLabel }> = ({ level }) => (
	<div className="bodyregular mt-2 text-character-secondary">
		Confidence:{' '}
		<span className={`bodymedium ${CONFIDENCE_TEXT_COLOR[level]}`}>
			{confidenceDisplayLabel(level)}
		</span>
	</div>
);

// -- Threshold colorers ------------------------------------------------------

const costsValueColor = (pct: number): string => {
	if (pct >= 70) return 'text-red-500';
	if (pct >= 50) return 'text-sunsetOrange-8';
	return 'text-character-primary';
};

const yoyColor = (absPct: number): string => {
	if (absPct >= 30) return 'text-red-500';
	if (absPct > 0) return 'text-sunsetOrange-8';
	return 'text-character-secondary';
};

const reliabilityValueColor = (pct: number): string => {
	if (pct <= 30) return 'text-red-500';
	if (pct <= 50) return 'text-sunsetOrange-8';
	return 'text-character-primary';
};

const usefulLifeValueColor = (pct: number): string => {
	if (pct >= 70) return 'text-red-500';
	if (pct >= 50) return 'text-sunsetOrange-8';
	return 'text-character-primary';
};

// -- Utility helpers ---------------------------------------------------------

const formatCondition = (raw: string): string => {
	const spaced = raw.replace(/([A-Z])/g, ' $1').trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const pluralUnit = (value: number, singular: string): string =>
	`${value} ${singular}${value === 1 ? '' : 's'}`;

const humanizeDuration = (seconds: number): string => {
	const hours = seconds / 3600;
	if (hours < 48) return pluralUnit(Math.round(hours), 'hour');
	const days = hours / 24;
	if (days < 60) return pluralUnit(Math.round(days), 'day');
	const months = days / 30.44;
	if (months < 24) return pluralUnit(Math.round(months), 'month');
	const years = days / 365.25;
	const trimmed = years.toFixed(1).replace(/\.0$/, '');
	return `${trimmed} ${trimmed === '1' ? 'year' : 'years'}`;
};

export const extractPercentage = (headline: string | null | undefined): number => {
	if (!headline) return 0;
	const match = headline.match(/(\d+)/);
	return match ? parseInt(match[1], 10) : 0;
};

const formatUsd = currencyFormatter(0, 'USD');

const assetTypeIdFrom = (asset: any): string | number | null => {
	if (!asset) return null;
	return nullSafeGetOrElse('assetType.id', asset, null);
};

// `score === null` means backend returned a shell dimension: the dimension is
// present but unscorable. Treat it like `insufficient_data` for display.
const isShell = (dim: { score: number | null } | null | undefined): boolean =>
	!!dim && (dim.score === null || dim.score === undefined);

// -- CTA list primitive ------------------------------------------------------

export const CtaLinkList: FC<{
	links: CtaLink[];
	onSelect?: (key: string) => void;
}> = ({ links, onSelect }) => {
	if (links.length === 0) return null;
	return (
		<div className="mt-2 flex flex-col gap-1">
			{links.map((link) =>
				onSelect ? (
					<a
						key={link.text}
						href="#"
						onClick={(event) => {
							event.preventDefault();
							onSelect(link.key);
						}}
						className="footnoteregular cursor-pointer text-character-secondary underline hover:text-purple-7 hover:underline"
					>
						{link.text} →
					</a>
				) : (
					<Link
						key={link.text}
						to={link.href}
						className="footnoteregular text-character-secondary underline hover:text-purple-7 hover:underline"
					>
						{link.text} →
					</Link>
				)
			)}
		</div>
	);
};

// -- Tile primitives ---------------------------------------------------------

interface TileShellProps {
	title: string;
	confidence: ConfidenceLabel;
	popoverContent: React.ReactNode;
	children: React.ReactNode;
}

export const TileShell: FC<TileShellProps> = ({ title, confidence, popoverContent, children }) => (
	<div className="h-full gap-xs rounded-md border border-solid border-grey-5 px-m py-s">
		<div className="bodyregular mb-2 flex items-center gap-1 text-character-secondary">
			<span>{title}</span>
			<Popover content={<div style={{ maxWidth: 280 }}>{popoverContent}</div>}>
				<ConfidenceIcon level={confidence} />
			</Popover>
		</div>
		{children}
	</div>
);

interface TileValueProps {
	headline: string | null;
	subtext: React.ReactNode | null;
	valueColor: string;
	ctaLinks?: CtaLink[];
	onCtaSelect?: (key: string) => void;
}

const TileValue: FC<TileValueProps> = ({
	headline,
	subtext,
	valueColor,
	ctaLinks,
	onCtaSelect,
}) => {
	const hasHeadline = headline !== null && headline !== undefined && headline.length > 0;
	return (
		<div>
			<div className={`h4medium ${hasHeadline ? valueColor : 'text-grey-7'}`}>
				{hasHeadline ? headline : '--'}
			</div>
			{subtext ? (
				<div className="footnoteregular mt-1 text-character-secondary">{subtext}</div>
			) : null}
			<CtaLinkList links={ctaLinks || []} onSelect={onCtaSelect} />
		</div>
	);
};

// -- Costs tile --------------------------------------------------------------

const deriveYoy = (
	dim: CostPressureDimension
): { pct: number | null; amount: number | null; windowText: string } | null => {
	const trend = nullSafeGet('components.trend', dim);
	if (!trend) return null;
	const slope = nullSafeGet('slopePerMonth', trend);
	const avg = nullSafeGet('avgMonthlySpend', trend);
	const numMonths = nullSafeGetOrElse('numMonthsWithSpend', trend, 0);
	if (slope === null || slope === undefined) return null;
	const windowText =
		numMonths >= 12
			? 'over past year'
			: numMonths === 1
			? 'over past 1 month'
			: `over past ${numMonths} months`;
	const amount = (slope as number) * 12;
	const pct = avg && (avg as number) > 0 ? ((slope as number) * 12) / (avg as number) : null;
	return { pct, amount, windowText };
};

export const CostsTile: FC<{
	dim: CostPressureDimension | null;
	asset?: any;
	assetId?: string | number;
	userType?: string;
	onCtaSelect?: (key: string) => void;
}> = ({ dim, asset, assetId, userType, onCtaSelect }) => {
	const confidence: ConfidenceLabel = nullSafeGetOrElse(
		'confidence.label',
		dim,
		'insufficient_data'
	);
	const headline = dim ? nullSafeGet('display.headline', dim) : null;
	const subtext = dim ? nullSafeGet('display.subtext', dim) : null;
	const pct = extractPercentage(headline);

	const missingInputs: string[] = nullSafeGetOrElse('confidence.missingInputs', dim, []);
	const ctaCtx = {
		assetId: assetId as string | number,
		assetTypeId: assetTypeIdFrom(asset),
		userType: userType,
	};
	const ctaLinks =
		!!asset && assetId !== undefined && assetId !== null
			? ctaLinksFromMissingInputs(missingInputs, ctaCtx)
			: [];
	const showCtasInBody = isShell(dim) || confidence === 'insufficient_data';

	const yoy = dim && !isShell(dim) ? deriveYoy(dim) : null;
	const showYoy =
		!!yoy && yoy.pct !== null && yoy.amount !== null && confidence !== 'insufficient_data';
	const yoyPct = showYoy ? (yoy!.pct as number) : 0;
	const yoyAmount = showYoy ? (yoy!.amount as number) : 0;
	const yoyAbsPct = Math.abs(Math.round(yoyPct * 100));
	const yoyDirection = yoyPct >= 0 ? '↑' : '↓';

	const popover = (
		<div>
			<div className="bodyregular">
				Measures total maintenance spend relative to the current replacement value of the asset.
			</div>
			<ConfidenceLine level={confidence} />
			{ctaLinks.length > 0 ? <CtaLinkList links={ctaLinks} onSelect={onCtaSelect} /> : null}
		</div>
	);

	const renderedSubtext =
		!isShell(dim) && subtext && asset ? (
			<Popover content={<AssetInvoice asset={asset} inline />} placement="bottom">
				<span className="cursor-help underline decoration-dotted">{subtext}</span>
			</Popover>
		) : isShell(dim) ? null : (
			subtext
		);

	return (
		<TileShell title="Repair / Replacement Costs" confidence={confidence} popoverContent={popover}>
			<TileValue
				headline={isShell(dim) ? null : headline}
				subtext={renderedSubtext}
				valueColor={costsValueColor(pct)}
				ctaLinks={showCtasInBody ? ctaLinks : []}
				onCtaSelect={onCtaSelect}
			/>
			{showYoy ? (
				<div className={`footnoteregular mt-1 ${yoyColor(yoyAbsPct)}`}>
					{yoyAbsPct === 0
						? 'No increase over past year'
						: `${yoyDirection} ${yoyAbsPct}% (${formatUsd(Math.abs(yoyAmount))}) ${
								yoy!.windowText
						  }`}
				</div>
			) : null}
		</TileShell>
	);
};

// -- Failure Risk tile (backed by DowntimeDimension) -------------------------

// Render a single `{value} {↓|↑} {pct} ({diff}) v/s avg` metric row using
// backend-pre-formatted display strings. `higherIsBad` controls colour polarity:
// for MTBR (higher = more reliable) `false`; for Mean Downtime (higher = worse)
// `true`.
const MetricRow: FC<{
	label: string;
	value: string | null;
	direction: 'up' | 'down' | null;
	diffPct: string | null;
	diff: string | null;
	higherIsBad: boolean;
}> = ({ label, value, direction, diffPct, diff, higherIsBad }) => {
	if (!value) return null;
	const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : null;
	const hasDelta = arrow !== null && (!!diffPct || !!diff);
	const isBad = hasDelta && (higherIsBad ? direction === 'up' : direction === 'down');
	const deltaColor = hasDelta
		? isBad
			? 'text-red-500'
			: 'text-green-600'
		: 'text-character-secondary';
	return (
		<div className="bodyregular">
			<div className="text-character-secondary">{label}</div>
			<div className="mt-xxs">
				<span className="bodymedium text-character-primary">{value}</span>
				{hasDelta ? (
					<span className={`footnoteregular ml-xs ${deltaColor}`}>
						{arrow} {diffPct}
						{diff ? ` (${diff})` : ''} v/s avg
					</span>
				) : null}
			</div>
		</div>
	);
};

export const ReliabilityTile: FC<{ dim: DowntimeDimension | null }> = ({ dim }) => {
	const confidence: ConfidenceLabel = nullSafeGetOrElse(
		'confidence.label',
		dim,
		'insufficient_data'
	);
	const headline = dim ? nullSafeGet('display.headline', dim) : null;
	const subtext = dim ? nullSafeGet('display.subtext', dim) : null;
	const pct = extractPercentage(headline);

	const mtbrValue = dim ? nullSafeGet('display.mtbrValue', dim) : null;
	const mtbrDir = dim ? nullSafeGet('display.mtbrDirection', dim) : null;
	const mtbrPct = dim ? nullSafeGet('display.mtbrDiffPct', dim) : null;
	const mtbrDiff = dim ? nullSafeGet('display.mtbrDiff', dim) : null;
	const dtValue = dim ? nullSafeGet('display.meanDtValue', dim) : null;
	const dtDir = dim ? nullSafeGet('display.meanDtDirection', dim) : null;
	const dtPct = dim ? nullSafeGet('display.meanDtDiffPct', dim) : null;
	const dtDiff = dim ? nullSafeGet('display.meanDtDiff', dim) : null;
	const hasAnyMetric = !!mtbrValue || !!dtValue;

	const popover = (
		<div style={{ maxWidth: 300 }}>
			<div className="bodyregular text-character-primary">Weighted average of:</div>
			{hasAnyMetric ? (
				<div className="mt-2 flex flex-col gap-xs">
					<MetricRow
						label="Mean Time Between Repairs (MTBR)"
						value={mtbrValue}
						direction={mtbrDir}
						diffPct={mtbrPct}
						diff={mtbrDiff}
						higherIsBad={false}
					/>
					<MetricRow
						label="Mean Downtime"
						value={dtValue}
						direction={dtDir}
						diffPct={dtPct}
						diff={dtDiff}
						higherIsBad={true}
					/>
				</div>
			) : null}
			{hasAnyMetric ? (
				<div className="footnoteregular mt-3 text-character-secondary">
					Please note: Ranks against assets with the same asset types.
				</div>
			) : null}
			<ConfidenceLine level={confidence} />
		</div>
	);

	return (
		<TileShell title="Failure Risk" confidence={confidence} popoverContent={popover}>
			<TileValue
				headline={isShell(dim) ? null : headline}
				subtext={isShell(dim) ? null : subtext}
				valueColor={reliabilityValueColor(pct)}
			/>
		</TileShell>
	);
};

// -- Useful Life tile --------------------------------------------------------

export const UsefulLifeTile: FC<{
	dim: RemainingLifeDimension | null;
	asset?: any;
	assetId?: string | number;
	userType?: string;
	onCtaSelect?: (key: string) => void;
}> = ({ dim, asset, assetId, userType, onCtaSelect }) => {
	const confidence: ConfidenceLabel = nullSafeGetOrElse(
		'confidence.label',
		dim,
		'insufficient_data'
	);
	const headline = dim ? nullSafeGet('display.headline', dim) : null;
	const backendSubtext = dim ? nullSafeGet('display.subtext', dim) : null;
	const consumedText = dim ? nullSafeGet('display.consumedText', dim) : null;
	const conditionRaw = dim ? nullSafeGet('components.condition.rawValue', dim) : null;

	const pct = extractPercentage(headline);
	let subtext = backendSubtext;
	if (!subtext && conditionRaw) {
		const remaining = Math.max(0, 100 - pct);
		subtext = `${remaining}% remaining life with ${formatCondition(
			conditionRaw as string
		).toLowerCase()} condition`;
	}

	const missingInputs: string[] = nullSafeGetOrElse('confidence.missingInputs', dim, []);
	const ctaCtx = {
		assetId: assetId as string | number,
		assetTypeId: assetTypeIdFrom(asset),
		userType: userType,
	};
	const ctaLinks =
		!!asset && assetId !== undefined && assetId !== null
			? ctaLinksFromMissingInputs(missingInputs, ctaCtx)
			: [];
	const showCtasInBody = isShell(dim) || confidence === 'insufficient_data';

	const popover = (
		<div>
			<div className="bodyregular">
				Estimates the useful life consumed based on the installation date and expected useful life.
			</div>
			<ConfidenceLine level={confidence} />
			{ctaLinks.length > 0 ? <CtaLinkList links={ctaLinks} onSelect={onCtaSelect} /> : null}
		</div>
	);

	return (
		<TileShell title="Useful Life Consumed" confidence={confidence} popoverContent={popover}>
			<TileValue
				headline={isShell(dim) ? null : headline}
				subtext={isShell(dim) ? null : consumedText || subtext}
				valueColor={usefulLifeValueColor(pct)}
				ctaLinks={showCtasInBody ? ctaLinks : []}
				onCtaSelect={onCtaSelect}
			/>
		</TileShell>
	);
};
